/**
 * Client for founder-authored in-app announcements (the one-time modal).
 * Mirrors the self-contained fetch style of `features/billing/lib/billing.ts`.
 */

import { backendUrl } from "@/shared/config";
import { loadTokens } from "@/features/auth/lib/tokenStorage";

export interface Announcement {
  id: string;
  title: string;
  body: string; // markdown
  ctaLabel: string | null;
  ctaUrl: string | null;
}

interface RawAnnouncement {
  id: string;
  title: string;
  body: string;
  cta_label: string | null;
  cta_url: string | null;
}

/** Exported for tests. */
export function mapAnnouncement(raw: RawAnnouncement): Announcement {
  return {
    id: raw.id,
    title: raw.title,
    body: raw.body,
    ctaLabel: raw.cta_label,
    ctaUrl: raw.cta_url,
  };
}

async function authedRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const tokens = loadTokens();
  if (!tokens) throw new Error("Not signed in");
  const res = await fetch(backendUrl(path), {
    ...init,
    headers: {
      ...(init.headers as Record<string, string> | undefined),
      Authorization: `Bearer ${tokens.accessToken}`,
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Announcement request failed (${res.status}): ${text}`);
  }
  return res.json() as Promise<T>;
}

/** The active announcement for this user, or null if there's nothing to show. */
export async function getActiveAnnouncement(): Promise<Announcement | null> {
  const raw = await authedRequest<RawAnnouncement | null>("/announcements/active");
  return raw ? mapAnnouncement(raw) : null;
}

/** Mark an announcement dismissed so it won't fire again for this user. */
export async function markAnnouncementSeen(id: string): Promise<void> {
  await authedRequest(`/announcements/${id}/seen`, { method: "POST" });
}
